import { t } from '@i18n';
import { org_sector_type_enum } from '@predium/enums';
import isNil from 'lodash/isNil';

// Types matching the backend action (camelCase) response shape for getFolderContents
export type DriveFolder = {
  id: number;
  name: string;
  parentId?: number | null;
  orgId: number;
  folderTypeId: string;
  portfolioId?: number | null;
  economicUnitId?: number | null;
  buildingId?: number | null;
  createdAt: string;
  updatedAt: string;
  childFolderCount: number;
  fileCount: number;
};

export type DriveFile = {
  id: number;
  name?: string | null;
  createdAt: string;
  uploading: boolean;
  energyCertificates: Array<{ id: number; buildingId?: number | null; buildingDraftId?: number | null }>;
  consumptionInvoices: Array<{ id: number; buildingId: number }>;
  emissionCertificates: Array<{ id: number }>;
  energyCertificateDrafts: Array<{ id: number; buildingDraft: { id: number } }>;
  consumptionInvoiceDrafts: Array<{ id: number; buildingId: number }>;
  emissionCertificateDrafts: Array<{ id: number }>;
};

export type DriveFileMembership = {
  id: number;
  fileId: number;
  folderId: number;
  createdAt: string;
  file: DriveFile;
};

export const FOLDER_TYPES = {
  PORTFOLIOS: 'PORTFOLIOS',
  PORTFOLIO: 'PORTFOLIO',
  ECONOMIC_UNIT: 'ECONOMIC_UNIT',
  BUILDING: 'BUILDING',
  EMISSION_CERTIFICATES: 'EMISSION_CERTIFICATES',
  ENERGY_CERTIFICATES: 'ENERGY_CERTIFICATES',
  CONSUMPTION_INVOICES: 'CONSUMPTION_INVOICES',
  USER_CREATED: 'USER_CREATED',
} as const;

export type SmartFileType =
  | 'energy_certificate'
  | 'energy_certificate_draft'
  | 'consumption_invoice'
  | 'consumption_invoice_draft'
  | 'emission_certificate'
  | 'emission_certificate_draft'
  | null;

export function getSmartFileType(file: DriveFile): SmartFileType {
  if (file.energyCertificates.length > 0) return 'energy_certificate';
  if (file.energyCertificateDrafts.length > 0) return 'energy_certificate_draft';
  if (file.consumptionInvoices.length > 0) return 'consumption_invoice';
  if (file.consumptionInvoiceDrafts.length > 0) return 'consumption_invoice_draft';
  if (file.emissionCertificates.length > 0) return 'emission_certificate';
  if (file.emissionCertificateDrafts.length > 0) return 'emission_certificate_draft';
  return null;
}

export interface LazyFolderNode {
  id: number;
  name: string;
  parent_id: number | null;
  folder_type_id: string;
  childCount: number;
  buildingId: number | null;
  portfolioId: number | null;
  economicUnitId: number | null;
  children?: LazyFolderNode[];
  childrenLoaded: boolean;
}

/** Walk UP from targetId via parent_id. Returns true if sourceId is an ancestor. */
export function isAncestorOf(
  sourceId: number,
  targetId: number,
  nodeMap: Map<number, { parent_id: number | null }>,
): boolean {
  const visited = new Set<number>();
  let currentId: number | null = targetId;
  while (currentId !== null && !visited.has(currentId)) {
    if (currentId === sourceId) return true;
    visited.add(currentId);
    const node = nodeMap.get(currentId);
    currentId = node?.parent_id ?? null;
  }
  return false;
}

/** Whether this folder is system-managed (not user-created). */
export function isSystemFolder(folderTypeId: string): boolean {
  return folderTypeId !== FOLDER_TYPES.USER_CREATED;
}

export function getSystemFolderTranslation(folderTypeId: string): string | null {
  switch (folderTypeId) {
    case FOLDER_TYPES.PORTFOLIOS:
      return t('Drive_FolderName_Portfolios');
    case FOLDER_TYPES.ENERGY_CERTIFICATES:
      return t('Drive_FolderName_EnergyCertificates');
    case FOLDER_TYPES.CONSUMPTION_INVOICES:
      return t('Drive_FolderName_ConsumptionInvoices');
    case FOLDER_TYPES.EMISSION_CERTIFICATES:
      return t('Drive_FolderName_EmissionCertificates');
    default:
      return null;
  }
}

export function getFolderIcon(folderTypeId: string): string {
  switch (folderTypeId) {
    case FOLDER_TYPES.USER_CREATED:
      return '/icons/ic-folder.svg';
    case FOLDER_TYPES.BUILDING:
      return '/icons/ic-folder-building.svg';
    case FOLDER_TYPES.ECONOMIC_UNIT:
      return '/icons/ic-folder-economic-unit.svg';
    case FOLDER_TYPES.PORTFOLIOS:
    case FOLDER_TYPES.PORTFOLIO:
      return '/icons/ic-folder-portfolio.svg';
    case FOLDER_TYPES.ENERGY_CERTIFICATES:
      return '/icons/ic-folder-energy.svg';
    case FOLDER_TYPES.CONSUMPTION_INVOICES:
      return '/icons/ic-folder-consumption.svg';
    case FOLDER_TYPES.EMISSION_CERTIFICATES:
      return '/icons/ic-folder-emission.svg';
    default:
      return '/icons/ic-folder-locked.svg';
  }
}

/** Short type label used to disambiguate folders (e.g. "Building", "Economic Unit"). */
export function getFolderTypeLabel(folderTypeId: string, orgSector: org_sector_type_enum): string | null {
  switch (folderTypeId) {
    case FOLDER_TYPES.BUILDING:
      return t('Drive_Type_Building');
    case FOLDER_TYPES.ECONOMIC_UNIT:
      return t('Drive_Type_EconomicUnit', { orgSector });
    default:
      return null;
  }
}

/**
 * Folder display name, suffixed with its type (e.g. "Atelierstraße 29 (Building)") when it shares
 * its parent's name — disambiguates economic-unit/building pairs. Shared by the tree and breadcrumb.
 */
export function getDisambiguatedFolderName(
  folder: { name: string; folder_type_id: string },
  parentName: string | null | undefined,
  orgSector: org_sector_type_enum,
): string {
  const resolvedName = getSystemFolderTranslation(folder.folder_type_id) ?? folder.name;
  const typeLabel = getFolderTypeLabel(folder.folder_type_id, orgSector);
  const sharesParentName = !isNil(parentName) && parentName.trim() === folder.name.trim();
  return sharesParentName && typeLabel ? `${resolvedName} (${typeLabel})` : resolvedName;
}
